import {
  useModalContext,
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSIcon,
  WSModal,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ISubscription,
  SubscriptionPackage
} from "@wingspanhq/users/dist/lib/interfaces";
import Tooltip from "@material-ui/core/Tooltip";
import React from "react";
import { useUserId } from "../../query/hooks/helpers";
import { useSubscriptionV3Query } from "../../query/subscriptions/queries";
import {
  isFeatureIncludedInSubscription,
  WSFrontendFeature
} from "../../Settings/utils/subscriptionUtils";
import { MEMBERSHIP_UPGRADE_MODAL } from "./UpgradeModal";
import styles from "./FeatureProtector.module.scss";
import { makeStyles, Theme } from "@material-ui/core";
import { getAccountIsPaused } from "../../query/subscriptions/selectors";
import classNames from "classnames";

export const PERSISTENT_UPGRADE_MODAL = "PERSISTENT_UPGRADE_MODAL";

export interface FeatureProtectorProps extends WSElementProps {
  feature?: WSFrontendFeature;
  isBlockedForPausedAccount?: boolean;
  isBlocked?: boolean;
}

export const FeatureProtector: React.FC<FeatureProtectorProps> = ({
  feature = WSFrontendFeature.None,
  isBlockedForPausedAccount = true,
  isBlocked,
  className,
  ...otherProps
}) => {
  const userId = useUserId();
  const subscriptionQuery = useSubscriptionV3Query(userId);
  const { openModal } = useModalContext();

  const isFeatureIncluded = isFeatureIncludedInSubscription(
    feature,
    subscriptionQuery.data as ISubscription
  );

  const isAccountPaused = isBlockedForPausedAccount
    ? getAccountIsPaused(subscriptionQuery.data)
    : false;

  const isFeatureEnabled = isFeatureIncluded && !isAccountPaused && !isBlocked;

  const tooltipContent = (
    <WSFlexBox direction="column">
      <WSFlexBox
        direction="row"
        justify="flex-start"
        alignItems="stretch"
        mb="M"
      >
        <WSFlexBox.Center className={styles.upgradeIconContainer} mr="S">
          <WSIcon name="outlineArrowUp" color="red400" size="M" />
        </WSFlexBox.Center>
        <WSFlexBox direction="column">
          <WSText.Heading3>Upgrade to gain access</WSText.Heading3>
          <WSText.Label color="grey700">Get this feature and more</WSText.Label>
        </WSFlexBox>
      </WSFlexBox>
      <WSButton
        fullWidth
        onClick={() =>
          openModal(MEMBERSHIP_UPGRADE_MODAL, {
            packageName: SubscriptionPackage.Professional
          })
        }
      >
        Get feature
      </WSButton>
    </WSFlexBox>
  );
  const useStylesBootstrap = makeStyles((theme: Theme) => ({
    arrow: {
      color: theme.palette.common.white,
      boxShadow: "0px 4px 20px rgba(171, 174, 181, 0.5)"
    },
    tooltip: {
      backgroundColor: theme.palette.common.white,
      padding: theme.spacing(2),
      boxShadow: "0px 4px 20px rgba(171, 174, 181, 0.5)"
    }
  }));
  const classes = useStylesBootstrap();

  const handleMount = (element: HTMLElement) => {
    if (element) {
      const handle = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        (window.document.activeElement as HTMLInputElement)?.blur();
        return false;
      };

      element.addEventListener("submit", handle, true);
      element.addEventListener("focus", handle, true);
    }
  };

  return isFeatureEnabled || feature === WSFrontendFeature.None ? (
    <WSElement key="enabled" className={className} {...otherProps} />
  ) : (
    <>
      <WSScreen.TabletAndDesktop>
        <Tooltip
          classes={classes}
          interactive
          placement="bottom"
          arrow
          title={tooltipContent}
        >
          <WSElement
            key="disabledDesktop"
            ref={handleMount}
            {...otherProps}
            className={classNames(className, styles.blocked)}
            onClick={() => openModal(PERSISTENT_UPGRADE_MODAL)}
          />
        </Tooltip>
      </WSScreen.TabletAndDesktop>
      <WSScreen.Mobile>
        <WSModal name={PERSISTENT_UPGRADE_MODAL}>{tooltipContent}</WSModal>
        <WSElement
          key="disabledMobile"
          ref={handleMount}
          {...otherProps}
          className={classNames(className, styles.blocked)}
          onClick={() => openModal(PERSISTENT_UPGRADE_MODAL)}
        />
      </WSScreen.Mobile>
    </>
  );
};
